import React, {useState, useEffect, useContext, useRef} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {useForm} from 'react-hook-form'
import {useIntl, defineMessage, FormattedMessage} from 'react-intl'
import {
    Button,
    Box,
    Radio,
    Spinner,
    Flex,
    Text,
    useMultiStyleConfig,
    Divider,
    useMediaQuery,
} from '@chakra-ui/react'
import {
    LocatorIcon,
    TelephoneIcon,
    WebsiteIcon,
    ChevronUp,
    CloseIcon,
    FiltersIcon,
} from '../../components/icons'
import Checkbox from '../../components/checkbox/index'
import {remCalc} from '../../theme/_utils'
import LocationMap from '../../components/location-map'
import {StoreLocatorContext, useStoreLocator} from '../../components/store-locator-modal/index'
import {useSearchStores} from '@salesforce/commerce-sdk-react'
import {STORE_LOCATOR_NUM_STORES_PER_LOAD, STORE_LOCATOR_DISTANCE_UNIT} from '../../constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import QuickLinks from '../faq/partials/quick-links'
import {useGetPageByType} from '../../custom-api/hooks/use-get-page-by-type'
import {useLocaleCountry} from '../../hooks/use-locale-custom'
import {countryFallbacks, getFallbackCoordinates} from '../../utils/googleMapsUtils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'
import LocationSearchBar from '../../components/location-map/location-search-bar'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../custom-api/layers/default'

const useGeolocation = (currentCountry) => {
    const {
        setSearchStoresParams,
        setAutomaticGeolocationHasFailed,
        setUserHasSetManualGeolocation,
        userHasSetManualGeolocation,
    } = useContext(StoreLocatorContext)

    const getGeolocationError = () => {
        setAutomaticGeolocationHasFailed(true)
        const fallback = countryFallbacks[currentCountry] || countryFallbacks['IT'] // Default: Italia

        //fallback
        setSearchStoresParams({
            latitude: fallback.latitude,
            longitude: fallback.longitude,
            limit: STORE_LOCATOR_NUM_STORES_PER_LOAD,
        })
    }
    const getGeolocationSuccess = (position) => {
        setAutomaticGeolocationHasFailed(false)
        setSearchStoresParams({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            limit: STORE_LOCATOR_NUM_STORES_PER_LOAD,
        })
    }

    const getUserGeolocation = () => {
        if (navigator?.geolocation) {
            navigator.geolocation.getCurrentPosition(getGeolocationSuccess, getGeolocationError)
            setUserHasSetManualGeolocation(false)
        } else {
            return 'Geolocation not supported'
        }
    }

    useEffect(() => {
        if (!userHasSetManualGeolocation) getUserGeolocation()
    }, [])

    return getUserGeolocation
}

const useHidePoweredByGoogle = () => {
    useEffect(() => {
        const style = document.createElement('style')
        style.innerHTML = `
        .pac-logo::after {
          display: none !important;
          content: none !important;
        }
        input.pac-target-input {
            width: 100% !important;
        }
        input.pac-target-input:focus, input.pac-target-input:focus-visible {
            outline: none !important;
            box-shadow: none !important;
            border-color: inherit !important;
        }
      `
        document.head.appendChild(style)

        return () => {
            document.head.removeChild(style)
        }
    }, [])
}

const radioButtonsInitialState = [
    {value: 25, label: '25 Km', isSelected: false, zoomLevel: 11},
    {value: 50, label: '50 Km', isSelected: false, zoomLevel: 10},
    {value: 100, label: '100 Km', isSelected: false, zoomLevel: 9},
]

export const checkboxMessages = {
    AC: defineMessage({
        defaultMessage: 'Air Conditioning',
        id: 'service-center-locator.label.air-conditioning',
    }),
    AT: defineMessage({
        defaultMessage: 'Air Treatment',
        id: 'service-center-locator.label.air-treatment',
    }),
    CE: defineMessage({
        defaultMessage: 'Coffee',
        id: 'service-center-locator.label.coffee',
    }),
    CO: defineMessage({
        defaultMessage: 'Comfort',
        id: 'service-center-locator.label.comfort',
    }),
    FC: defineMessage({
        defaultMessage: 'Floorcare',
        id: 'service-center-locator.label.floorcare',
    }),
    FA: defineMessage({
        defaultMessage: 'Fully Automatic Coffee Machines',
        id: 'service-center-locator.label.fully-automatic-coffee-machines',
    }),
    HE: defineMessage({
        defaultMessage: 'Heating',
        id: 'service-center-locator.label.heating',
    }),
    HS: defineMessage({
        defaultMessage: 'Home Service',
        id: 'service-center-locator.label.home-service',
    }),
    HA: defineMessage({
        defaultMessage: 'Home Appliances',
        id: 'service-center-locator.label.home-appliances',
    }),
    IR: defineMessage({
        defaultMessage: 'Ironing',
        id: 'service-center-locator.label.ironing',
    }),
    MJ: defineMessage({
        defaultMessage: 'Major Kitchen Appliances',
        id: 'service-center-locator.label.major-kitchen-appliances',
    }),
    ME: defineMessage({
        defaultMessage: 'Major Kitchen Appliances Electric',
        id: 'service-center-locator.label.major-kitchen-appliances-electric',
    }),
    MG: defineMessage({
        defaultMessage: 'Major Kitchen Appliances Electric & Gas',
        id: 'service-center-locator.label.major-kitchen-appliances-electric-gas',
    }),
    NE: defineMessage({
        defaultMessage: 'NESPRESSO',
        id: 'service-center-locator.label.nespresso',
    }),
}

const checkboxButtonInitialState = [
    {value: 'AC', isSelected: false}, // Air Conditioning
    {value: 'AT', isSelected: false}, // Air Treatment
    {value: 'CE', isSelected: false}, // Coffee
    {value: 'CO', isSelected: false}, // Comfort
    {value: 'FC', isSelected: false}, // Floorcare
    {value: 'FA', isSelected: false}, // Fully Automatic Coffee Machines
    {value: 'HE', isSelected: false}, // Heating
    {value: 'HS', isSelected: false}, // Home Service
    {value: 'HA', isSelected: false}, // Home Appliances
    {value: 'IR', isSelected: false}, // Ironing
    {value: 'MJ', isSelected: false}, // Major Kitchen Appliances
    {value: 'ME', isSelected: false}, // Major Kitchen Appliances Electric
    {value: 'MG', isSelected: false}, // Major Kitchen Appliances Electric & Gas
    {value: 'NE', isSelected: false}, // NESPRESSO
]

const distanceInitialState = 1000

const AuthorizedServiceCenter = () => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('ServiceCenterLocator')
    const currentCountry = useLocaleCountry().toUpperCase()
    const {locale} = useMultiSite()
    const [isMobile] = useMediaQuery(`(max-width: ${remCalc(1023)})`)
    const {data: contentstackData} = useGetPageByType('faq_page')
    const autocompleteRef = useRef(null)
    const fallbackCoordinates = getFallbackCoordinates(currentCountry)
    const lastSearchedPlaceId = useRef(null)
    const {customParams} = getConfig()
    const location = useLocation()
    const currentPath = 'https://' + customParams.currentHost + location.pathname

    const authorizedServiceCenterMessage = defineMessage({
        defaultMessage: 'Authorized Service Center',
        id: 'service-center-locator.label.title',
    })

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: intl.formatMessage(authorizedServiceCenterMessage),
        description: intl.formatMessage(authorizedServiceCenterMessage),
        url: currentPath,
    }

    const [stores, setStores] = useState([])
    const [selectedStore, setSelectedStore] = useState(null)
    const [toggleInfoWindow, setToggleInfoWindow] = useState(false)
    const [showFiltersSection, setShowFiltersSection] = useState(true)
    const [checkboxButtons, setCheckboxButtons] = useState(checkboxButtonInitialState)
    const [radioButtons, setRadioButtons] = useState(radioButtonsInitialState)
    const [distance, setDistance] = useState(distanceInitialState)
    const [lastSearchedDistance, setLastSearchedDistance] = useState(null)
    const [showMap, setShowMap] = useState(true)
    const [offset, setOffset] = useState(0)
    const [allFetchedStore, setAllFetchedStore] = useState([])
    const [hasMoreStores, setHasMoreStores] = useState(true)
    const [showHideMapButton, setShowHideMapButton] = useState(true)
    const [isSearchTriggered, setIsSearchTriggered] = useState(false)
    const [searchedLocation, setSearchedLocation] = useState({})
    const [isZeroResults, setIsZeroResults] = useState(false)
    const [accordionState, setAccordionState] = useState({
        distanceFilters: true,
        serviceFilters: true,
    })
    const [visibleStoresCount, setVisibleStoresCount] = useState(5) // Initial count of visible stores

    const handleViewMoreStores = () => {
        setVisibleStoresCount((prevCount) => prevCount + 5) // Increment by 5 on each click
    }

    const NUM_STORES_PER_REQUEST_API_MAX = 200

    useHidePoweredByGoogle()

    useEffect(() => {
        const findSelectedRadioButton = radioButtons.find((elm) => elm.isSelected)
        if (findSelectedRadioButton) {
            setDistance(findSelectedRadioButton.value)
        }
    }, [radioButtons])

    const handleSelectedStore = (store) => {
        setToggleInfoWindow(false)
        setTimeout(() => {
            setSelectedStore(store)
            setToggleInfoWindow(true)
        }, 0)

        const updatedStores = stores.map((el) => ({
            ...el,
            isSelected: el.id === store.id,
        }))
        setStores(updatedStores)
    }

    const getActiveFiltersCount = () => {
        const activeCheckboxFilters = checkboxButtons.filter((button) => button.isSelected).length
        const activeRadioFilters = radioButtons.filter((button) => button.isSelected).length
        return activeCheckboxFilters + activeRadioFilters
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const getUserGeolocation = useGeolocation(currentCountry)

    const {
        searchStoresParams,
        setSearchStoresParams,
        userHasSetManualGeolocation,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
        setUserHasSetManualGeolocation,
    } = useContext(StoreLocatorContext)

    const {
        data: searchStoresData,
        isLoading,
        isFetching,
        refetch,
    } = useSearchStores({
        parameters: {
            countryCode: currentCountry,
            postalCode: searchStoresParams.postalCode || undefined,
            latitude: searchStoresParams.latitude,
            longitude: searchStoresParams.longitude,
            locale: locale?.id || 'default',
            maxDistance: distance,
            limit: NUM_STORES_PER_REQUEST_API_MAX,
            distanceUnit: STORE_LOCATOR_DISTANCE_UNIT,
            offset: offset,
        },
    })

    useEffect(() => {
        if (allFetchedStore?.length < searchStoresData?.total) {
            setHasMoreStores(true)
        }
        if (searchStoresData?.data?.length > 0 && hasMoreStores) {
            setAllFetchedStore((prevStores) => {
                const updatedStores = [...prevStores, ...searchStoresData.data]

                if (updatedStores.length >= searchStoresData.total) {
                    setHasMoreStores(false)
                    setOffset(0)
                } else {
                    setOffset((prevOffset) => prevOffset + NUM_STORES_PER_REQUEST_API_MAX)
                }

                const uniqueStores = updatedStores.filter(
                    (store, index, self) => index === self.findIndex((s) => s.id === store.id),
                )

                return uniqueStores
            })
        } else {
            if (searchStoresData?.data?.length > 0 && isSearchTriggered) {
                setAllFetchedStore(searchStoresData.data)
                setIsSearchTriggered(false)
                setIsZeroResults(false)
            }
            if (
                (searchStoresData?.total === 0 || searchStoresData?.data.length === 0) &&
                isSearchTriggered
            ) {
                setAllFetchedStore([])
                setDistance(1000)
                setSearchedLocation((prev) => ({
                    ...prev,
                    zoom: 5,
                }))

                refetch()
                setIsSearchTriggered(false)
                setIsZeroResults(true)
            }
        }
    }, [searchStoresData, hasMoreStores, allFetchedStore.length])

    useEffect(() => {
        const filteredStores = allFetchedStore.filter((store) => {
            const matchesCountry = store.countryCode === currentCountry
            const matchesDistance = store.distance <= distance
            const matchesServices = checkboxButtons.every(
                (checkbox) =>
                    !checkbox.isSelected ||
                    (Array.isArray(store?.c_services) && store.c_services.includes(checkbox.value)),
            )
            return matchesCountry && matchesDistance && matchesServices
        })
        setStores(filteredStores)
    }, [allFetchedStore, currentCountry, distance, checkboxButtons])

    useEffect(() => {
        if (stores.length > 0) {
            setSearchedLocation({
                lat: stores[0].latitude,
                lng: stores[0].longitude,
            })
        }
    }, [checkboxButtons])

    const form = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            countryCode: userHasSetManualGeolocation ? searchStoresParams.countryCode : 'IT',
            postalCode: userHasSetManualGeolocation ? searchStoresParams.postalCode : '',
        },
    })

    const handlePlaceSelected = async (place) => {
        if (place.geometry) {
            if (lastSearchedPlaceId.current !== place.place_id) {
                fireServiceLocatorEvent('search')
                lastSearchedPlaceId.current = place.place_id
            }
            setShowHideMapButton(false)
            const latitude = place.geometry.location.lat()
            const longitude = place.geometry.location.lng()
            const postalCode =
                place.address_components.find((comp) => comp.types?.includes('postal_code'))
                    ?.long_name || null

            autocompleteRef.current = {
                latitude,
                longitude,
                postalCode,
            }
            setSearchedLocation({
                lat: latitude,
                lng: longitude,
            })
            await submitForm()
        }
    }

    const submitForm = async () => {
        setDistance(50)
        setLastSearchedDistance(distance)

        if (autocompleteRef.current) {
            const {latitude, longitude, postalCode} = autocompleteRef.current
            setSearchStoresParams((prev) => ({
                ...prev,
                latitude,
                longitude,
                postalCode: postalCode || '',
                limit: STORE_LOCATOR_NUM_STORES_PER_LOAD,
            }))
        }

        setIsSearchTriggered(true)
    }

    const handleFilters = () => {
        setShowFiltersSection(!showFiltersSection)
    }

    const handleRadioButtonClick = (el, index) => {
        const updatedRadioButtons = radioButtons.map((elm, i) => {
            if (index === i) {
                return {
                    ...elm,
                    isSelected: !elm.isSelected,
                }
            } else {
                return {
                    ...elm,
                    isSelected: false,
                }
            }
        })
        const isAnyRadioButtonSelected = updatedRadioButtons.some((button) => button.isSelected)

        setSearchedLocation({
            lat: searchedLocation.lat,
            lng: searchedLocation.lng,
            zoom: isAnyRadioButtonSelected ? el.zoomLevel : 10,
        })
        setRadioButtons(updatedRadioButtons)
        setIsSearchTriggered(true)
    }

    const handleCheckboxClick = (service, index) => {
        const updatedCheckboxes = checkboxButtons.map((elm, i) => {
            if (index === i) {
                return {
                    ...elm,
                    isSelected: !elm.isSelected,
                }
            }
            return elm
        })
        setCheckboxButtons(updatedCheckboxes)
        setIsSearchTriggered(true)
    }

    const handleRemoveAllButton = () => {
        setCheckboxButtons(checkboxButtonInitialState)
        setRadioButtons(radioButtonsInitialState)
        setDistance(lastSearchedDistance || distanceInitialState)
        setIsSearchTriggered(false)
    }

    const removeFilters = (filter) => {
        const filteredCheckbox = checkboxButtons.map((elm) => {
            if (elm.value === filter.value) {
                return {
                    ...elm,
                    isSelected: !elm.isSelected,
                }
            }
            return elm
        })
        setCheckboxButtons(filteredCheckbox)
        const filteredRadioButtons = radioButtons.map((button) => {
            if (button.value === filter.value) {
                return {
                    ...button,
                    isSelected: !button.isSelected,
                }
            }
            return button
        })
        setRadioButtons(filteredRadioButtons)

        const noFiltersApplied =
            filteredCheckbox.every((elm) => !elm.isSelected) &&
            filteredRadioButtons.every((button) => !button.isSelected)

        if (noFiltersApplied) {
            setDistance(lastSearchedDistance || distanceInitialState)
        }
    }

    const handleUserLocation = () => {
        handleRemoveAllButton()
        setDistance(100)
        setAllFetchedStore([])
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(async (position) => {
                const {latitude, longitude} = position.coords

                setSearchStoresParams((prev) => ({
                    ...prev,
                    latitude,
                    longitude,
                    postalCode: '',
                    limit: STORE_LOCATOR_NUM_STORES_PER_LOAD,
                }))
                setSearchedLocation({lat: position.coords.latitude, lng: position.coords.longitude})

                fireServiceLocatorEvent('nearMe')
            })
        }
    }

    const handleHideMapButton = () => {
        setShowMap(!showMap)
    }

    const toggleAccordion = (section) => {
        if (isMobile) {
            setAccordionState((prevState) => ({
                ...prevState,
                [section]: !prevState[section],
            }))
        }
    }

    const renderDistanceFilters = (radioButtons, handleRadioButtonClick, styles, intl) => (
        <Box {...styles.filterSectionContainerMobile}>
            <Flex
                {...styles.filterByDistance}
                paddingBottom={accordionState.distanceFilters ? remCalc(24) : 0}
                data-testid="distance-filters"
                onClick={() => toggleAccordion('distanceFilters')}
                cursor="pointer"
            >
                <Text {...styles.distanceTitleContainer}>
                    {intl.formatMessage({
                        defaultMessage: 'Distance',
                        id: 'service-center-locator.label.distance',
                    })}
                </Text>
                <ChevronUp
                    display={{base: 'block', lg: 'none'}}
                    transform={accordionState.distanceFilters ? 'rotate(0deg)' : 'rotate(180deg)'}
                    transition="transform 0.2s ease"
                />
            </Flex>
            {accordionState.distanceFilters && (
                <Flex {...styles.distanceContainerMobile}>
                    {radioButtons.length > 0 &&
                        radioButtons.map((el, index) => (
                            <Flex {...styles.radioButtonsContainer} key={index}>
                                <Flex {...styles.radioButtons}>
                                    <Text>{el.label}</Text>
                                    <Radio
                                        isChecked={el.isSelected}
                                        {...styles.radioButton}
                                        onClick={() => handleRadioButtonClick(el, index)}
                                    />
                                </Flex>
                            </Flex>
                        ))}
                </Flex>
            )}
        </Box>
    )

    const renderServiceFilters = (
        checkboxButtons,
        handleCheckboxClick,
        styles,
        intl,
        isMobile,
        checkboxMessages,
    ) => (
        <Box {...styles.filterSectionContainerMobile} borderBottom={0}>
            <Flex
                {...styles.checkboxesTitleContainer}
                paddingBottom={accordionState.serviceFilters ? remCalc(24) : 0}
                onClick={() => toggleAccordion('serviceFilters')}
                cursor="pointer"
            >
                <Text {...styles.checkboxTitle}>
                    {intl.formatMessage({
                        defaultMessage: 'Type of service',
                        id: 'service-center-locator.label.service-type',
                    })}
                </Text>
                <ChevronUp
                    display={{base: 'block', lg: 'none'}}
                    transform={accordionState.serviceFilters ? 'rotate(0deg)' : 'rotate(180deg)'}
                    transition="transform 0.2s ease"
                />
            </Flex>
            {accordionState.serviceFilters && (
                <Flex
                    {...styles.checkboxesContainer}
                    sx={{
                        overflowY: isMobile ? 'scroll' : 'unset',
                        '&::-webkit-scrollbar': {
                            width: remCalc(0),
                        },
                    }}
                >
                    {checkboxButtons &&
                        checkboxButtons.map((service, index) => (
                            <Flex key={index} flexDir="column" gap={remCalc(16)}>
                                <Flex {...styles.checkboxFilterItem}>
                                    <Text id={service?.value}>
                                        {intl.formatMessage(checkboxMessages[service.value])}
                                    </Text>
                                    <Checkbox
                                        {...styles.checkbox}
                                        isChecked={service?.isSelected}
                                        _checked={{
                                            '& .chakra-checkbox__control': {
                                                boxShadow: 'none',
                                            },
                                        }}
                                        onChange={() => handleCheckboxClick(service, index)}
                                    />
                                </Flex>
                            </Flex>
                        ))}
                </Flex>
            )}
        </Box>
    )

    const renderShowMoreResultsButton = (styles, intl) => (
        <Button
            {...styles.showMoreResultsButton}
            onClick={() => {
                setShowFiltersSection(!showFiltersSection)
            }}
        >
            {intl.formatMessage(
                {
                    defaultMessage: 'See {result} results',
                    id: 'service-center-locator.label.results',
                },
                {
                    result: stores?.length > 0 ? stores.length : '0',
                },
            )}
        </Button>
    )

    const {addLayer} = useGoogleTagManager()
    const fireServiceLocatorEvent = (label) => {
        addLayer(
            basic_gtm_event.bind(this, {
                event: 'serviceLocator',
                eventCategory: 'afterSales',
                eventAction: 'serviceLocator',
                eventLabel: label,
            }),
        )
    }

    return (
        <Box
            data-testid="service-center-container"
            layerStyle="page"
            data-gtm="service-locator-page"
        >
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            <Flex {...styles.serviceCenterContainer}>
                <Flex {...styles.titleContainer}>
                    <Button
                        as={Link}
                        variant={'text-button'}
                        {...styles.button}
                        to={`/${locale?.alias}`}
                    >
                        {intl.formatMessage({
                            defaultMessage: 'Back to homepage',
                            id: 'service-center-locator.button.go-back',
                        })}
                    </Button>
                    <Text {...styles.title}>
                        {intl.formatMessage(authorizedServiceCenterMessage)}
                    </Text>
                </Flex>
                <Divider mb={remCalc(24)} />
                <Flex {...styles.mapsLocatorContainer}>
                    <Flex {...styles.serviceCenters}>
                        <Flex {...styles.serviceCenterLocator}>
                            <form id="store-locator-form" onSubmit={form.handleSubmit(submitForm)}>
                                <LocationSearchBar onPlaceSelected={handlePlaceSelected} />
                            </form>
                            <Flex
                                as="button"
                                {...styles.locator}
                                onClick={handleUserLocation}
                                aria-label={intl.formatMessage({
                                    defaultMessage: 'Use my location',
                                    id: 'service-center-locator.button.use-location',
                                })}
                            >
                                <Text {...styles.buttonLabel}>
                                    {intl.formatMessage({
                                        defaultMessage: 'Use my location',
                                        id: 'service-center-locator.button.use-location',
                                    })}
                                </Text>
                                <LocatorIcon />
                            </Flex>
                            {showHideMapButton && showMap && (
                                <Button {...styles.hideMapButton} onClick={handleHideMapButton}>
                                    {intl.formatMessage({
                                        defaultMessage: 'Hide Map',
                                        id: 'service-center-locator.button.hide-map',
                                    })}
                                </Button>
                            )}
                            {showHideMapButton && !showMap && (
                                <Button {...styles.hideMapButton} onClick={handleHideMapButton}>
                                    {intl.formatMessage({
                                        defaultMessage: 'Show Map',
                                        id: 'service-center-locator.button.show-map',
                                    })}
                                </Button>
                            )}
                        </Flex>
                        {(isLoading || isFetching) && !showHideMapButton && (
                            <Flex {...styles.spinner} data-testid="loading-spinner">
                                <Spinner size="lg" />
                            </Flex>
                        )}
                        {(!isLoading || !isFetching) &&
                            !showHideMapButton &&
                            searchStoresData?.data?.length > 0 && (
                                <Box
                                    {...styles.filterContainerMobile}
                                    position={
                                        !showFiltersSection ? {base: 'fixed', lg: 'unset'} : 'unset'
                                    }
                                    bg={
                                        !showFiltersSection
                                            ? {base: '#000000cc', lg: 'unset'}
                                            : 'unset'
                                    }
                                >
                                    <Box
                                        {...styles.filtersMobileVersion}
                                        position={
                                            !showFiltersSection
                                                ? {base: 'absolute', lg: 'unset'}
                                                : 'unset'
                                        }
                                        borderTopRadius={
                                            !showFiltersSection
                                                ? {base: remCalc(10), lg: 'unset'}
                                                : ''
                                        }
                                        height={
                                            !showFiltersSection
                                                ? {base: remCalc(632), lg: 'unset'}
                                                : 'unset'
                                        }
                                    >
                                        {searchStoresData?.data?.length > 0 && (
                                            <Box {...styles.filtersButtonMainContainer}>
                                                <Flex {...styles.filtersContainer}>
                                                    <Flex
                                                        data-testid="service-center-button"
                                                        {...styles.filterIconContainer}
                                                        maxW={
                                                            !showFiltersSection
                                                                ? {base: '100%', lg: remCalc(212)}
                                                                : 'unset'
                                                        }
                                                        margin={
                                                            !showFiltersSection
                                                                ? {base: '0 auto', lg: 'unset'}
                                                                : 'unset'
                                                        }
                                                        onClick={handleFilters}
                                                        justifyContent={
                                                            showFiltersSection
                                                                ? {
                                                                      base: 'flex-end',
                                                                      lg: 'space-between',
                                                                  }
                                                                : {
                                                                      base: 'center',
                                                                      lg: 'space-between',
                                                                  }
                                                        }
                                                        borderBottom={
                                                            !showFiltersSection
                                                                ? {
                                                                      base: '1px solid #D4D4D4',
                                                                      lg: 'none',
                                                                  }
                                                                : 'unset'
                                                        }
                                                        bg={
                                                            !showFiltersSection
                                                                ? {
                                                                      base: 'unset',
                                                                      lg: 'background.light',
                                                                  }
                                                                : 'background.light'
                                                        }
                                                    >
                                                        <Text
                                                            alignContent="center"
                                                            display={
                                                                !showFiltersSection
                                                                    ? {base: 'none', lg: 'block'}
                                                                    : 'unset'
                                                            }
                                                        >
                                                            {intl.formatMessage({
                                                                defaultMessage: 'Filters',
                                                                id: 'service-center-locator.button.filters',
                                                            })}{' '}
                                                            {getActiveFiltersCount() > 0 &&
                                                                `(${getActiveFiltersCount()})`}
                                                        </Text>
                                                        {!showFiltersSection && (
                                                            <Flex {...styles.filterPopUpContainer}>
                                                                <Text {...styles.filterPopUpTitle}>
                                                                    {intl.formatMessage({
                                                                        defaultMessage:
                                                                            'Filters and Sort',
                                                                        id: 'service-center-locator.button.filters-and-sort',
                                                                    })}
                                                                </Text>
                                                                {!showFiltersSection && (
                                                                    <CloseIcon
                                                                        {...styles.filterPopUpCloseIcon}
                                                                    />
                                                                )}
                                                            </Flex>
                                                        )}
                                                        {showFiltersSection && <FiltersIcon />}
                                                        {!showFiltersSection && (
                                                            <ChevronUp {...styles.chevronUpIcon} />
                                                        )}
                                                    </Flex>
                                                    <Text
                                                        {...styles.removeAllButton}
                                                        onClick={() => {
                                                            handleRemoveAllButton(
                                                                searchStoresData?.data,
                                                            )
                                                        }}
                                                        data-testid="remove-all-button"
                                                    >
                                                        {intl.formatMessage({
                                                            defaultMessage: 'Remove all',
                                                            id: 'service-center-locator.button.remove-all',
                                                        })}
                                                    </Text>
                                                </Flex>
                                                {/* bottoni filtri selezionati */}
                                                {showFiltersSection &&
                                                    (radioButtons.find((elm) => elm.isSelected) ||
                                                        checkboxButtons.find(
                                                            (elm) => elm.isSelected,
                                                        )) && (
                                                        <Flex {...styles.selectedFilterContainer}>
                                                            {radioButtons.map((item, index) => {
                                                                if (item.isSelected) {
                                                                    return (
                                                                        <Flex
                                                                            {...styles.selectedFilterItem}
                                                                            key={index}
                                                                        >
                                                                            <CloseIcon
                                                                                {...styles.checkboxCloseIcon}
                                                                                onClick={() => {
                                                                                    removeFilters(
                                                                                        item,
                                                                                    )
                                                                                }}
                                                                            />
                                                                            <Text
                                                                                {...styles?.selectedFilterValue}
                                                                            >
                                                                                {item.value}
                                                                            </Text>
                                                                        </Flex>
                                                                    )
                                                                }
                                                            })}
                                                            {/* </Flex>
                                                    )} */}
                                                            {checkboxButtons.map((item, index) => {
                                                                if (item.isSelected) {
                                                                    return (
                                                                        <Flex
                                                                            {...styles.selectedFilterItem}
                                                                            key={index}
                                                                            display={
                                                                                !item.isSelected
                                                                                    ? 'none'
                                                                                    : 'flex'
                                                                            }
                                                                        >
                                                                            <CloseIcon
                                                                                {...styles.checkboxCloseIcon}
                                                                                onClick={() => {
                                                                                    removeFilters(
                                                                                        item,
                                                                                    )
                                                                                }}
                                                                            />
                                                                            <Text
                                                                                {...styles?.selectedFilterValue}
                                                                            >
                                                                                {intl.formatMessage(
                                                                                    checkboxMessages[
                                                                                        item.value
                                                                                    ],
                                                                                )}
                                                                            </Text>
                                                                        </Flex>
                                                                    )
                                                                }
                                                            })}

                                                            <Button
                                                                {...styles.removeAllButton}
                                                                onClick={() => {
                                                                    handleRemoveAllButton(
                                                                        searchStoresData?.data,
                                                                    )
                                                                }}
                                                                display={{
                                                                    base: 'block',
                                                                    lg: 'none',
                                                                }}
                                                                my={{base: 'unset', lg: 'unset'}}
                                                                data-testid="remove-all-button"
                                                            >
                                                                {intl.formatMessage({
                                                                    defaultMessage: 'Remove all',
                                                                    id: 'service-center-locator.button.remove-all',
                                                                })}
                                                            </Button>
                                                        </Flex>
                                                    )}
                                                {/* Store found Label */}
                                                <Text
                                                    {...styles.storeResults}
                                                    display={!showFiltersSection ? 'none' : 'block'}
                                                >
                                                    {isZeroResults ? 0 : stores?.length}{' '}
                                                    {intl.formatMessage({
                                                        defaultMessage: 'Results found',
                                                        id: 'service-center-locator.search-results',
                                                    })}
                                                </Text>
                                            </Box>
                                        )}
                                        {!showFiltersSection && (
                                            <>
                                                <Flex
                                                    flexDir="column"
                                                    {...styles.filterItemsContainer}
                                                    maxH={{lg: remCalc(400)}}
                                                    mt={
                                                        radioButtons.every(
                                                            (elm) => elm.isSelected,
                                                        ) ||
                                                        checkboxButtons.every(
                                                            (elm) => elm.isSelected,
                                                        )
                                                            ? 'unset'
                                                            : remCalc(8)
                                                    }
                                                    sx={{
                                                        '&::-webkit-scrollbar': {
                                                            width: remCalc(15),
                                                            backgroundColor: 'background.light',
                                                            border: '4px solid transparent',
                                                        },
                                                        '&::-webkit-scrollbar-thumb': {
                                                            backgroundColor:
                                                                'button.border.inverse-primary-focused',
                                                            borderRadius: remCalc(10),
                                                            border: '4px solid #f0f0f0',
                                                        },
                                                        '&::-webkit-scrollbar-track': {
                                                            backgroundColor: 'background.light',
                                                        },
                                                    }}
                                                    alignItems={{base: 'center', lg: 'unset'}}
                                                >
                                                    {renderDistanceFilters(
                                                        radioButtons,
                                                        handleRadioButtonClick,
                                                        styles,
                                                        intl,
                                                    )}
                                                    {renderServiceFilters(
                                                        checkboxButtons,
                                                        handleCheckboxClick,
                                                        styles,
                                                        intl,
                                                        isMobile,
                                                        checkboxMessages,
                                                    )}
                                                </Flex>
                                                <Box {...styles.showMoreResultsContainer}>
                                                    {renderShowMoreResultsButton(styles, intl)}
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        <Flex
                            {...styles.mapContainer}
                            height={remCalc(572)}
                            display={{base: showMap === true ? 'flex' : 'none', lg: 'none'}}
                        >
                            <LocationMap
                                stores={stores}
                                selectedStore={selectedStore}
                                toggleToolTip={toggleInfoWindow}
                                setSelectedStore={setSelectedStore}
                                fallbackCoordinates={fallbackCoordinates}
                                searchedLocation={searchedLocation}
                            />
                        </Flex>
                        {showFiltersSection && (
                            <Flex
                                {...styles.serviceCenterAddressContainer}
                                sx={{
                                    '&::-webkit-scrollbar': {
                                        width: remCalc(15),
                                        backgroundColor: 'background.light',
                                        border: '4px solid transparent',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'button.border.inverse-primary-focused',
                                        borderRadius: remCalc(10),
                                        border: '4px solid #f0f0f0',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: 'background.light',
                                    },
                                }}
                                display={showFiltersSection ? 'flex' : 'none'}
                            >
                                <Flex {...styles.serviceCenterAddressMainContainer}>
                                    {(!isLoading || !isFetching) &&
                                        stores?.length > 0 &&
                                        stores
                                            .slice(0, isMobile ? visibleStoresCount : stores.length)
                                            .map(
                                                (
                                                    store, // Slice the stores array
                                                ) => (
                                                    <Flex
                                                        {...styles.serviceCenterAddress}
                                                        key={store.id}
                                                        onClick={() => handleSelectedStore(store)}
                                                        border={
                                                            store.isSelected
                                                                ? '2px solid'
                                                                : '1px solid'
                                                        }
                                                        borderColor={
                                                            store.isSelected
                                                                ? 'border.default'
                                                                : 'border.table'
                                                        }
                                                    >
                                                        <Text
                                                            fontSize={remCalc(20)}
                                                            fontWeight="500"
                                                        >
                                                            {store.name}
                                                        </Text>
                                                        <Flex
                                                            flexDir="column"
                                                            lineHeight={remCalc(16)}
                                                        >
                                                            <Text>{store.address1}</Text>
                                                            <Flex>
                                                                {store.city && (
                                                                    <Text>{store.city + ', '}</Text>
                                                                )}
                                                                {store.postalCode && (
                                                                    <Text>
                                                                        {store.postalCode + ', '}
                                                                    </Text>
                                                                )}
                                                                {store.stateCode && (
                                                                    <Text>
                                                                        {store.stateCode + ', '}
                                                                    </Text>
                                                                )}
                                                                {store.countryCode && (
                                                                    <Text>{store.countryCode}</Text>
                                                                )}
                                                            </Flex>
                                                            {store?.c_services?.map(
                                                                (service, index) => (
                                                                    <Text
                                                                        key={index}
                                                                        color="text.secondary"
                                                                        {...styles.addressDescription}
                                                                    >
                                                                        {intl.formatMessage(
                                                                            checkboxMessages[
                                                                                service
                                                                            ],
                                                                        )}
                                                                        {index <
                                                                        store.c_services.length - 1
                                                                            ? ', '
                                                                            : ''}
                                                                    </Text>
                                                                ),
                                                            )}
                                                        </Flex>
                                                        <Flex {...styles.serviceCenterContact}>
                                                            <TelephoneIcon
                                                                {...styles.addressIcons}
                                                            />
                                                            <Text
                                                                as="a"
                                                                href={`tel:${store.phone}`}
                                                            >
                                                                {store.phone}
                                                            </Text>
                                                        </Flex>
                                                        <Flex
                                                            display={{base: 'flex', lg: 'none'}}
                                                            {...styles.serviceCenterContact}
                                                        >
                                                            <WebsiteIcon {...styles.addressIcons} />
                                                            <Text>{store?.email}</Text>
                                                        </Flex>
                                                        <Flex {...styles.serviceCenterContact}>
                                                            <LocatorIcon
                                                                w={remCalc(11)}
                                                                {...styles.addressIcons}
                                                            />
                                                            <Text>
                                                                {store.distance +
                                                                    store.distanceUnit}
                                                            </Text>
                                                        </Flex>
                                                        <Button
                                                            {...styles.mapTooltipContactButton}
                                                            {...styles.contactStoreButton}
                                                            as="a"
                                                            href={
                                                                store?.email
                                                                    ? `mailto:${store.email}`
                                                                    : undefined
                                                            }
                                                        >
                                                            {intl.formatMessage({
                                                                defaultMessage: 'Contact us',
                                                                id: 'service-center-locator.info-popup-contact-button',
                                                            })}
                                                        </Button>
                                                        <Button
                                                            {...styles.tooltipGoToMapsButton}
                                                            {...styles.contactStoreButton}
                                                        >
                                                            {intl.formatMessage({
                                                                defaultMessage: 'Google maps',
                                                                id: 'service-center-locator.info-popup-google-maps-button',
                                                            })}
                                                        </Button>
                                                    </Flex>
                                                ),
                                            )}
                                    {isMobile && stores?.length > visibleStoresCount && (
                                        <Flex {...styles.viewMoreContainer}>
                                            <Text {...styles.viewMoreText}>
                                                <FormattedMessage
                                                    defaultMessage="You are viewing {alreadyLoadedItems} service centers out of {totalItems}"
                                                    id="service-center-locator.info-popup-and-more-stores"
                                                    values={{
                                                        alreadyLoadedItems: visibleStoresCount,
                                                        totalItems: stores?.length ?? 0,
                                                    }}
                                                />
                                            </Text>
                                            <Button
                                                onClick={handleViewMoreStores}
                                                variant={'secondary'}
                                                {...styles.viewMoreButton}
                                            >
                                                {intl.formatMessage({
                                                    defaultMessage: 'View More',
                                                    id: 'service-center-locator.button.view-more',
                                                })}
                                            </Button>
                                        </Flex>
                                    )}
                                </Flex>
                            </Flex>
                        )}
                    </Flex>
                    <Flex
                        {...styles.mapContainer}
                        height={remCalc(572)}
                        display={{base: 'none', lg: 'flex'}}
                    >
                        <LocationMap
                            stores={stores}
                            selectedStore={selectedStore}
                            toggleToolTip={toggleInfoWindow}
                            setSelectedStore={setSelectedStore}
                            fallbackCoordinates={fallbackCoordinates}
                            searchedLocation={searchedLocation}
                        />
                    </Flex>
                </Flex>
            </Flex>
            <Divider mt={{base: remCalc(24), lg: remCalc(48)}} />
            <Flex w="100%">
                {contentstackData && (
                    <QuickLinks
                        quickLinksData={contentstackData?.bottom_components[0]?.quick_link}
                        title={contentstackData?.bottom_components[0]?.links_title}
                    />
                )}
            </Flex>
        </Box>
    )
}

const AuthorizedServiceCenterContainer = () => {
    const storeLocator = useStoreLocator()
    return (
        <StoreLocatorContext.Provider value={storeLocator}>
            <AuthorizedServiceCenter />
        </StoreLocatorContext.Provider>
    )
}

export default AuthorizedServiceCenterContainer
